import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import Small from 'UIComponents/elements/Small';
import { H2 } from 'UIComponents/elements/headings';
import UILink from 'UIComponents/link/UILink';
import UISection from 'UIComponents/section/UISection';
import ExamplePropertyMatchingInfoAlert from 'automation-ui-enrollment/components/eventEnrollment/steps/ExamplePropertyMatchingInfoAlert';
import WebhookPropertyMappingVisualization from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookPropertyMappingVisualization';
import WebhookObjectTypeSelect from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookObjectTypeSelect';
import useUniquePropertiesForObjectType from 'automation-ui-enrollment/hooks/useUniquePropertiesForObjectType';
import { getFilterGroups, getPropertyDropdownOptions } from 'automation-ui-enrollment/utils/webhookUtils';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { useEffect, useMemo, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSelectedWebhookPropertyType = (mappings, primaryAssociation) => {
  if (primaryAssociation) {
    const maybePropertyDef = mappings.find(mapping => mapping.property === primaryAssociation.webhookProperty);
    return maybePropertyDef && maybePropertyDef.type;
  }
  return null;
};
const getSelectedHubSpotPropertyType = (properties, primaryAssociation) => {
  if (primaryAssociation) {
    const maybePropertyDef = properties.find(({
      property
    }) => property.name === primaryAssociation.hubspotProperty);
    return maybePropertyDef && maybePropertyDef.property.type;
  }
  return null;
};
const WebhookEnrollExistingStep = ({
  allowObjectTypeSelection,
  objectTypeId,
  setObjectTypeId,
  propertyMappings,
  setDisableSubmit,
  setObjectAssociation,
  propertyValueDefinitions,
  setPropertyValueDefinitions
}) => {
  const [primaryAssociation, setPrimaryAssociation] = useState(propertyValueDefinitions[0]);
  const {
    data: uniqueProperties,
    loading: uniquePropertiesLoading,
    error: uniquePropertiesError,
    refetch: refetchUniqueProperties
  } = useUniquePropertiesForObjectType(objectTypeId);

  // Ensure the type of the currently selected property matches up with what property types are available
  // https://hubspot.slack.com/archives/C6SFPB69Z/p1706556565924779
  const selectedWebhookPropertyType = useMemo(() => getSelectedWebhookPropertyType(propertyMappings, primaryAssociation), [propertyMappings, primaryAssociation]);
  const selectedHubSpotPropertyType = useMemo(() => getSelectedHubSpotPropertyType(uniqueProperties || [], primaryAssociation), [uniqueProperties, primaryAssociation]);

  // Perform step validation
  useEffect(() => {
    if (!primaryAssociation || !primaryAssociation.hubspotProperty || !primaryAssociation.webhookProperty || selectedHubSpotPropertyType !== selectedWebhookPropertyType) {
      setDisableSubmit(true);
    } else {
      setDisableSubmit(false);
      const newObjectAssociation = {
        associationMode: 'USE_EXISTING_OBJECT',
        filterGroups: getFilterGroups([primaryAssociation])
      };
      setObjectAssociation(newObjectAssociation);
    }
  }, [primaryAssociation, setDisableSubmit, setObjectAssociation, selectedHubSpotPropertyType, selectedWebhookPropertyType]);

  // Auto-select the first property from the available property options
  useEffect(() => {
    if (uniqueProperties.length > 0) {
      const newAssociation = {
        webhookProperty: primaryAssociation ? primaryAssociation.webhookProperty : '',
        hubspotProperty: uniqueProperties[0].property.name
      };
      setPrimaryAssociation(newAssociation);
      setPropertyValueDefinitions([newAssociation]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueProperties]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H2, {
      className: "text-center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.webhookWizard.associatingObject.title"
      })
    }), !allowObjectTypeSelection && objectTypeId ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
      isJsx: true,
      baseLangKey: "automation-ui-enrollment.webhookWizard.associatingObject.description_cob",
      objectTypeId: objectTypeId,
      elements: {
        b: 'b'
      }
    }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-enrollment.webhookWizard.associatingObject.description"
    }), /*#__PURE__*/_jsxs(UISection, {
      className: "m-y-7",
      children: [allowObjectTypeSelection && /*#__PURE__*/_jsx(WebhookObjectTypeSelect, {
        objectTypeId: objectTypeId,
        setObjectTypeId: setObjectTypeId
      }), /*#__PURE__*/_jsx(WebhookPropertyMappingVisualization, {
        sourceLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.webhookWizard.associatingObject.propertyMatching.source.label"
        }),
        sourceSelectProps: {
          onChange: evt => {
            const newAssociation = {
              webhookProperty: evt.target.value,
              hubspotProperty: primaryAssociation ? primaryAssociation.hubspotProperty : ''
            };
            setPrimaryAssociation(newAssociation);
            setPropertyValueDefinitions([newAssociation]);
          },
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.webhookWizard.associatingObject.propertyMatching.source.placeholder"
          }),
          options: getPropertyDropdownOptions(propertyMappings, selectedHubSpotPropertyType),
          value: primaryAssociation && primaryAssociation.webhookProperty
        },
        destinationLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.webhookWizard.associatingObject.propertyMatching.destination.label"
        }),
        destinationSelectProps: {
          readOnly: Boolean(uniquePropertiesLoading || uniquePropertiesError || uniqueProperties.length <= 1),
          options: uniqueProperties.map(({
            property
          }) => ({
            text: property.label,
            value: property.name
          })),
          placeholder: '',
          onChange: evt => {
            const newAssociation = {
              webhookProperty: primaryAssociation ? primaryAssociation.webhookProperty : '',
              hubspotProperty: evt.target.value
            };
            const newHubSpotPropertyType = getSelectedHubSpotPropertyType(uniqueProperties, newAssociation);

            // Reset the selected webhook property if types don't match
            if (!!selectedWebhookPropertyType && newHubSpotPropertyType !== selectedWebhookPropertyType) {
              newAssociation.webhookProperty = '';
            }
            setPrimaryAssociation(newAssociation);
            setPropertyValueDefinitions([newAssociation]);
          },
          value: primaryAssociation && primaryAssociation.hubspotProperty
        },
        destinationHelpNode: uniquePropertiesError ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Small, {
            use: "error",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.webhookWizard.associatingObject.propertyMatching.destination.error.message"
            })
          }), /*#__PURE__*/_jsx(Small, {
            className: "m-left-2",
            use: "help",
            children: /*#__PURE__*/_jsx(UILink, {
              onClick: () => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                refetchUniqueProperties();
              },
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.webhookWizard.associatingObject.propertyMatching.destination.error.tryAgain"
              })
            })
          })]
        }) : undefined
      })]
    }), /*#__PURE__*/_jsx(ExamplePropertyMatchingInfoAlert, {})]
  });
};
export default WebhookEnrollExistingStep;